import {
  DrawingLayerLike,
  DrawingPaintToolLike,
  DrawingPointLike,
  DrawingStrokeLike,
  DrawingViewport,
  clampDrawingOpacity,
  clampDrawingPoint,
  drawingWeightInPixels,
} from './drawing-geometry.util';

export type DrawingRenderContext = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export interface DrawingRenderStroke extends DrawingStrokeLike {
  composite?: 'source-over' | 'destination-out';
}

export interface DrawingRenderLayer extends DrawingLayerLike {
  strokes: readonly DrawingRenderStroke[];
}

export interface DrawingRenderOptions {
  /** Clear the isolated drawing surface before replaying its vector operations. */
  clear?: boolean;
  /** Draft strokes rendered after persisted strokes for live gesture feedback. */
  extraStrokes?: readonly DrawingRenderStroke[];
}

export interface TimedDrawingLayer {
  startSeconds: number;
  endSeconds: number;
  layer: DrawingRenderLayer | null;
}

/** Active drawing layers in their canonical track/clip order. End is exclusive. */
export function activeDrawingLayers(
  drawings: readonly TimedDrawingLayer[],
  timeSeconds: number,
): DrawingRenderLayer[] {
  return drawings
    .filter(drawing => timeSeconds >= drawing.startSeconds && timeSeconds < drawing.endSeconds)
    .map(drawing => drawing.layer)
    .filter((layer): layer is DrawingRenderLayer => Boolean(layer?.strokes.length));
}

/**
 * Replays a vector drawing onto an EMPTY, ISOLATED drawing surface.
 *
 * This target must not already contain the underlying video/image. Erasers use
 * `destination-out`, so isolation guarantees they only remove drawing pixels. Composite
 * the completed layer surface over the video in a separate `drawImage` call.
 */
export function renderDrawingLayer(
  context: DrawingRenderContext,
  layer: DrawingRenderLayer | null,
  viewport: DrawingViewport,
  options: DrawingRenderOptions = {},
): void {
  const width = Math.max(0, viewport.width);
  const height = Math.max(0, viewport.height);
  if (options.clear !== false) context.clearRect(0, 0, width, height);
  if (width <= 0 || height <= 0) return;

  const strokes = [...(layer?.strokes ?? []), ...(options.extraStrokes ?? [])];
  for (const stroke of strokes) renderDrawingStroke(context, stroke, { width, height });
}

/**
 * Replays multiple active timeline drawing layers onto one isolated surface.
 * Later erasers remove earlier drawing pixels, while the underlying video remains untouched.
 */
export function renderDrawingLayers(
  context: DrawingRenderContext,
  layers: readonly DrawingRenderLayer[],
  viewport: DrawingViewport,
): void {
  const width = Math.max(0, viewport.width);
  const height = Math.max(0, viewport.height);
  context.clearRect(0, 0, width, height);
  if (width <= 0 || height <= 0) return;
  for (const layer of layers) {
    renderDrawingLayer(context, layer, { width, height }, { clear: false });
  }
}

export function renderDrawingStroke(
  context: DrawingRenderContext,
  stroke: DrawingRenderStroke,
  viewport: DrawingViewport,
): void {
  if (!stroke.points.length || viewport.width <= 0 || viewport.height <= 0) return;

  const points = stroke.points.map(clampDrawingPoint);
  const baseWidth = drawingWeightInPixels(stroke.weight, viewport);
  context.save();
  // Canonicalize from the tool instead of trusting a stale/malformed persisted value.
  context.globalCompositeOperation = compositeForRenderTool(stroke.tool);
  context.globalAlpha = stroke.tool === 'eraser' ? 1 : clampDrawingOpacity(stroke.opacity);
  context.strokeStyle = stroke.color || '#ffffff';
  context.fillStyle = stroke.color || '#ffffff';
  context.lineCap = 'round';
  context.lineJoin = 'round';

  if (points.length === 1) {
    drawDot(context, points[0], viewport, pressureWidth(baseWidth, points[0]));
  } else if (stroke.tool === 'pen' && points.some((point) => point.pressure !== undefined)) {
    renderPressureSegments(context, points, viewport, baseWidth);
  } else {
    renderSmoothPath(context, points, viewport, baseWidth);
  }
  context.restore();
}

/** Composite an already-rendered isolated layer over the destination video/image canvas. */
export function compositeDrawingSurface(
  destination: DrawingRenderContext,
  surface: CanvasImageSource,
  viewport: DrawingViewport,
  opacity = 1,
): void {
  if (viewport.width <= 0 || viewport.height <= 0) return;
  destination.save();
  destination.globalCompositeOperation = 'source-over';
  destination.globalAlpha = Math.min(1, Math.max(0, Number.isFinite(opacity) ? opacity : 1));
  destination.drawImage(surface, 0, 0, viewport.width, viewport.height);
  destination.restore();
}

function renderSmoothPath(
  context: DrawingRenderContext,
  points: readonly DrawingPointLike[],
  viewport: DrawingViewport,
  width: number,
): void {
  const first = toPixels(points[0], viewport);
  context.beginPath();
  context.moveTo(first.x, first.y);
  for (let index = 1; index < points.length - 1; index++) {
    const current = toPixels(points[index], viewport);
    const next = toPixels(points[index + 1], viewport);
    context.quadraticCurveTo(
      current.x,
      current.y,
      (current.x + next.x) / 2,
      (current.y + next.y) / 2,
    );
  }
  const last = toPixels(points.at(-1)!, viewport);
  context.lineTo(last.x, last.y);
  context.lineWidth = width;
  context.stroke();
}

function renderPressureSegments(
  context: DrawingRenderContext,
  points: readonly DrawingPointLike[],
  viewport: DrawingViewport,
  baseWidth: number,
): void {
  for (let index = 1; index < points.length; index++) {
    const startPoint = points[index - 1];
    const endPoint = points[index];
    const start = toPixels(startPoint, viewport);
    const end = toPixels(endPoint, viewport);
    context.beginPath();
    context.moveTo(start.x, start.y);
    context.lineTo(end.x, end.y);
    context.lineWidth =
      (pressureWidth(baseWidth, startPoint) + pressureWidth(baseWidth, endPoint)) / 2;
    context.stroke();
  }
}

function drawDot(
  context: DrawingRenderContext,
  point: DrawingPointLike,
  viewport: DrawingViewport,
  width: number,
): void {
  const pixel = toPixels(point, viewport);
  context.beginPath();
  context.arc(pixel.x, pixel.y, Math.max(0.25, width / 2), 0, Math.PI * 2);
  context.fill();
}

function pressureWidth(baseWidth: number, point: DrawingPointLike): number {
  if (point.pressure === undefined) return baseWidth;
  return baseWidth * (0.35 + 0.65 * point.pressure);
}

function toPixels(point: DrawingPointLike, viewport: DrawingViewport): { x: number; y: number } {
  return { x: point.x * viewport.width, y: point.y * viewport.height };
}

function compositeForRenderTool(tool: DrawingPaintToolLike): GlobalCompositeOperation {
  return tool === 'eraser' ? 'destination-out' : 'source-over';
}
